import React, { useMemo } from 'react';
import { formatNumber, formatDifference, formatRank, formatPercentile } from '../../utils/formatting';
import { getValueColor, getRateColor, getRateDiffColor } from '../../utils/coloring';
import { getCrimeDisplayName } from '../../constants/crimes';
import { getStateAbbreviation } from '../../constants/locations';
import './Tables.css';

const HistoricalRatesTable = ({ 
  selectedLocation, 
  selectedCrime, 
  selectedDateRange,
  setSelectedDateRange,
  setIsHistorical,
  selectedSource,
  compareMode
}) => {
  const { info, data } = selectedLocation;
  const locationType = info?.type?.charAt(0).toUpperCase() + info?.type?.slice(1);
  const isNation = info?.type?.toLowerCase() === 'nation';
  const isCounty = info?.type?.toLowerCase() === 'county';
  const isCity = info?.type?.toLowerCase() === 'city';
  const isMetro = info?.type?.toLowerCase() === 'metro';
  const isCountyOrCity = isCounty || isCity;
  
  const stateAbbr = useMemo(() => {
    if (isCountyOrCity && info?.name?.includes(',')) {
      const stateName = info.name.split(',')[1]?.trim();
      return getStateAbbreviation(stateName);
    }
    return getStateAbbreviation(selectedLocation);
  }, [isCountyOrCity, info?.name, selectedLocation]);

  const handleYearClick = (year) => {
    setSelectedDateRange(year.toString());
    setTimeout(() => {
      setIsHistorical(false);
    }, 100);
  };

  const getComparisonLabel = () => {
    if (isMetro) {
      return compareMode.rateType === 'metro' ? 'Metro' : 'National';
    }
    if (compareMode.rateType === 'county' || compareMode.rateType === 'city') {
      const locationTypeLabel = compareMode.rateType === 'county' ? 'County' : 'City';
      return compareMode.rateScope === 'state' ? `${stateAbbr} ${locationTypeLabel}` : `US ${locationTypeLabel}`;
    } else { 
      return compareMode.rateScope === 'state' ? `${stateAbbr} State` : 'US National';
    }
  };

  const transformData = () => {
    if (!data?.historical) {
      return [];
    }

    return data.historical
      .sort((a, b) => b.year - a.year)
      .map(yearData => {
        const crimeField = `${selectedCrime.toLowerCase()}_rate`;
        const rate = yearData[crimeField];
        
        if (rate === null || rate === undefined) return null;

        if (selectedSource === 'BJS_NCVS') {
          return {
            year: yearData.year,
            rate,
            person_population: yearData.person_population,
            household_population: yearData.household_population,
          };
        }

        let avgRateField, rankField, rateDiffField, rateDiffPctField, percentileField;
        
        if (isMetro) {
          rankField = `${selectedCrime.toLowerCase()}_national_rate_rank`;
          percentileField = `${selectedCrime.toLowerCase()}_national_rate_percentile`;
          if (compareMode.rateType === 'metro') {
            avgRateField = `${selectedCrime.toLowerCase()}_all_metros_rate`;
            rateDiffField = `${selectedCrime.toLowerCase()}_all_metros_rate_diff`;
            rateDiffPctField = `${selectedCrime.toLowerCase()}_all_metros_rate_diff_pct`;
          } else {
            avgRateField = `${selectedCrime.toLowerCase()}_national_rate`;
            rateDiffField = `${selectedCrime.toLowerCase()}_national_rate_diff`;
            rateDiffPctField = `${selectedCrime.toLowerCase()}_national_rate_diff_pct`;
          }
        } else if (isCountyOrCity) {
          if (compareMode.rateType === 'county' || compareMode.rateType === 'city') {
            const locationTypePlural = compareMode.rateType === 'county' ? 'counties' : 'cities';
            if (compareMode.rateScope === 'state') {
              rankField = `${selectedCrime.toLowerCase()}_state_rate_rank`;
              percentileField = `${selectedCrime.toLowerCase()}_state_rate_percentile`;
              avgRateField = `${selectedCrime.toLowerCase()}_all_${locationTypePlural}_state_rate`;
              rateDiffField = `${selectedCrime.toLowerCase()}_all_${locationTypePlural}_state_rate_diff`;
              rateDiffPctField = `${selectedCrime.toLowerCase()}_all_${locationTypePlural}_state_rate_diff_pct`;
            } else { 
              rankField = `${selectedCrime.toLowerCase()}_national_rate_rank`;
              percentileField = `${selectedCrime.toLowerCase()}_national_rate_percentile`;
              avgRateField = `${selectedCrime.toLowerCase()}_all_${locationTypePlural}_national_rate`;
              rateDiffField = `${selectedCrime.toLowerCase()}_all_${locationTypePlural}_national_rate_diff`;
              rateDiffPctField = `${selectedCrime.toLowerCase()}_all_${locationTypePlural}_national_rate_diff_pct`;
            }
          } else { 
            if (compareMode.rateScope === 'state') {
              rankField = `${selectedCrime.toLowerCase()}_state_rate_rank`;
              percentileField = `${selectedCrime.toLowerCase()}_state_rate_percentile`;
              avgRateField = `${selectedCrime.toLowerCase()}_state_rate`;
              rateDiffField = `${selectedCrime.toLowerCase()}_state_rate_diff`;
              rateDiffPctField = `${selectedCrime.toLowerCase()}_state_rate_diff_pct`;
            } else { 
              rankField = `${selectedCrime.toLowerCase()}_national_rate_rank`;
              percentileField = `${selectedCrime.toLowerCase()}_national_rate_percentile`;
              avgRateField = `${selectedCrime.toLowerCase()}_national_rate`;
              rateDiffField = `${selectedCrime.toLowerCase()}_national_rate_diff`;
              rateDiffPctField = `${selectedCrime.toLowerCase()}_national_rate_diff_pct`;
            }
          }
        } else {
          rankField = `${selectedCrime.toLowerCase()}_rate_rank`;
          percentileField = `${selectedCrime.toLowerCase()}_rate_percentile`;
          avgRateField = `${selectedCrime.toLowerCase()}_national_rate`;
          rateDiffField = `${selectedCrime.toLowerCase()}_national_rate_diff`;
          rateDiffPctField = `${selectedCrime.toLowerCase()}_national_rate_diff_pct`;
        }

        return {
          year: yearData.year,
          rate,
          population: yearData.population,
          comparisonRate: yearData[avgRateField],
          difference: yearData[rateDiffField],
          percentDiff: yearData[rateDiffPctField],
          rank: yearData[rankField],
          percentile: yearData[percentileField],
        };
      })
      .filter(row => row !== null);
  };

  const tableData = transformData();

  if (!tableData.length || (selectedSource === 'BJS_NCVS' && selectedCrime === 'all_crime')) {
    return (
      <div className="loading">
        {selectedSource === 'BJS_NCVS' && selectedCrime === 'all_crime' 
          ? <div style={{ marginTop: '-80px' }}>Historical Rates are not available for All Crime for<br />the National Crime Victimization Survey</div>
          : 'No Historical Data Available'
        }
      </div>
    );
  }

  return (
    <div className="main-table-wrapper">
      <div className="main-table-top">
        {selectedCrime === 'all_crime' ? 'All' : ''}&nbsp;{getCrimeDisplayName(selectedCrime, selectedSource)}&nbsp;Historical Rates
      </div>
      <div className="main-table-scroll-container">
        <table className={`main-table ${isNation ? 'nation-view' : ''} rates-table historical-table ${selectedSource === 'BJS_NCVS' ? 'ncvs-table' : ''}`}>
          <thead>
            <tr>
              <th>Year</th>
              {isNation ? (
                <>
                  {selectedSource === 'BJS_NCVS' ? (
                    <>
                      <th>
                        <div className="stacked-header">
                          <div>US Residents</div>
                          <div>Age 12 or Older</div>
                        </div>
                      </th>
                      <th>
                        <div className="stacked-header">
                          <div>US</div>
                          <div>Households</div>
                        </div>
                      </th>
                      <th>
                        <div className="stacked-header ncvs">
                          <div>{getCrimeDisplayName(selectedCrime, selectedSource)}</div>
                          <div>Rate</div>
                        </div>
                      </th>
                    </>
                  ) : (
                    <>
                      <th>
                        <div className="stacked-header">
                          <div>United States</div>
                          <div>Population</div>
                        </div>
                      </th>
                      <th>
                        <div className="single-line-header">{getCrimeDisplayName(selectedCrime)} Rate</div>
                      </th>
                    </>
                  )}
                </>
              ) : (
                <>
                  <th>
                    <div className="stacked-header">
                      <div>{locationType}</div>
                      <div>Population</div>
                    </div>
                  </th>
                  <th>
                    <div className="stacked-header">
                      <div>{getCrimeDisplayName(selectedCrime)}</div>
                      <div>Rate</div>
                    </div>
                  </th>
                </>
              )}
              {!isNation && (
                <>
                  <th>
                    <div className="stacked-header">
                      <div>{(isCountyOrCity || isMetro) ? getComparisonLabel() : 'National'}</div>
                      <div>Rate</div>
                    </div>
                  </th>
                  <th>
                    <div className="stacked-header">
                      <div>{(isCountyOrCity || isMetro) ? getComparisonLabel() : 'National'} Rate</div>
                      <div>Difference</div>
                    </div>
                  </th>
                  <th>
                    <div className="stacked-header">
                      <div>% {(isCountyOrCity || isMetro) ? getComparisonLabel() : 'National'} Rate</div>
                      <div>Difference</div>
                    </div>
                  </th>
                  <th>
                    <div className="stacked-header">
                      <div>{isCountyOrCity ? getComparisonLabel() : locationType}</div>
                      <div>Rank</div>
                    </div>
                  </th>
                  <th>
                    <div className="stacked-header">
                      <div>{isCountyOrCity ? getComparisonLabel() : locationType}</div>
                      <div>Percentile</div>
                    </div>
                  </th>
                </>
              )}
            </tr>
          </thead>
          <tbody>
            {tableData.map((row) => {
              return (
                <tr 
                  key={row.year}
                  className={row.year === parseInt(selectedDateRange) ? 'selected-row' : ''}
                >
                  <td>
                    <span onClick={() => handleYearClick(row.year)}>
                      {row.year}
                    </span>
                  </td>
                  {selectedSource === 'BJS_NCVS' ? (
                    <>
                      <td className="number-cell">
                        {formatNumber(row.person_population)}
                      </td>
                      <td className="number-cell">
                        {formatNumber(row.household_population)}
                      </td>
                    </>
                  ) : (
                    <td className="number-cell">
                      {formatNumber(row.population)}
                    </td>
                  )}
                  <td className={`number-cell ${isNation ? 'nation' : ''}`} style={{ color: getRateColor(row.rate, row.comparisonRate) }}>
                    {formatNumber(row.rate)}
                  </td>
                  {!isNation && (
                    <>
                      <td className="number-cell">{formatNumber(row.comparisonRate)}</td>
                      <td className="number-cell bold" style={{ color: getRateDiffColor(row.difference) }}>
                        {formatDifference(row.difference, false, true)}
                      </td>
                      <td className="number-cell" style={{ color: getValueColor(row.percentDiff) }}>
                        {formatDifference(row.percentDiff, true)}
                      </td>
                      <td className="rank-cell">{formatRank(row.rank)}</td>
                      <td className="rank-cell">{formatPercentile(row.percentile)}</td>
                    </>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default HistoricalRatesTable;